import { Bucket } from './taskBuckets';

/**
 * Цвета доски задач — ОДИН источник на доску, список и Гант (ТЗ 0065).
 *
 * 🔴 Зачем отдельный файл, а не hex по месту. До него один и тот же признак был
 * покрашен по-разному в двух видах одного экрана:
 *
 *   - статус «в работе» — `#1e3a8a` на карточке доски и `#1d4ed8` в строке
 *     списка;
 *   - высокий приоритет — КРАСНЫЙ на карточке и ЯНТАРНЫЙ в списке.
 *
 * Это не вкусовщина: человек читает цвет как признак, и два цвета для одного
 * признака означают, что признаков как будто два. Файлов было два
 * (`TaskBoard.styled` и `TaskList.styled`), и правка одного не доезжала до
 * второго — расхождение появилось само и молча. Тест
 * `__tests__/taskTones.test.ts` следит, чтобы каждая колонка и каждый статус
 * имели тон и чтобы список не завёл свой.
 *
 * Литералы Tailwind (slate/blue/amber/violet/emerald) оставлены сознательно:
 * весь «красивый» слой ERP — Zoom, карьерный центр, дофинансирование — держит
 * ту же палитру так же. Перевод ОДНОГО модуля на `services/ColorService` завёл
 * бы третью систему рядом с двумя существующими, а не убрал бы одну.
 */

export interface ITaskTone {
  /** Текст и значок. */
  fg: string;
  /** Мягкая заливка под текст: чип, шапка колонки. */
  bg: string;
  /** Линия: подчёркивание шапки, рамка, заливка полосы Ганта. */
  line: string;
}

export const NEUTRAL_TONE: ITaskTone = {
  fg: 'var(--k-fg-475569)',
  bg: 'var(--k-bg-f1f5f9)',
  line: 'var(--k-border-cbd5e1)',
};

/**
 * Тон «без тона»: так шапка колонки выглядела до ТЗ 0065.
 *
 * 🔴 Нужен не доске задач, а её СОСЕДУ: воронка дофинансирования
 * (`Dofinansowanie/parts/board`) переиспользует `B.Column`/`B.ColumnHead`
 * доски и своих тонов не имеет. Сделать `$tone` обязательным значило бы
 * покрасить чужой экран заодно.
 */
export const PLAIN_TONE: ITaskTone = {
  fg: 'var(--k-fg-475569)',
  bg: 'transparent',
  line: 'var(--k-border-e2e8f0)',
};

/** Ещё тише нейтрального: подписи групп «через неделю», «без срока» — они
 *  ориентируют, но не зовут. */
export const MUTED_TONE: ITaskTone = {
  // Текст — #64748b (контраст ≥ 4.5 на светлом фоне); #94a3b8 остаётся
  // только линиям и рамкам (ТЗ 0109, ревью).
  fg: 'var(--k-fg-64748b)',
  bg: 'var(--k-bg-f8fafc)',
  line: 'var(--k-border-e2e8f0)',
};

/**
 * Три уровня глубины. До ТЗ 0065 их было полтора: белая рабочая область и
 * колонки `#f8fafc` — разница в один процент светлоты, и границы колонок
 * читались как единственный разделитель. Теперь фон серый, колонка светлее
 * фона, карточка белая.
 */
export const SURFACE = {
  /** Фон рабочей области — под колонками. */
  app: 'var(--k-bg-eef2f7)',
  /** Колонка. */
  column: 'var(--k-bg-f8fafc)',
  /** Карточка, строка списка, окно. */
  card: 'var(--k-bg-ffffff)',
  line: 'var(--k-border-e2e8f0)',
  lineStrong: 'var(--k-border-cbd5e1)',
  text: 'var(--k-fg-0f172a)',
  muted: 'var(--k-fg-64748b)',
  mutedSoft: 'var(--k-bg-94a3b8)',
  accent: 'var(--k-fg-2563eb)',
  accentSoft: 'var(--k-bg-eff6ff)',
  accentLine: 'var(--k-border-93c5fd)',
};

/**
 * Тона колонок по срокам. Раскрашены ВСЕ ПЯТЬ: раньше цвет был только у
 * «Просроченных» и «Сегодня», а три оставшиеся колонки не отличались друг от
 * друга ничем, кроме подписи, — и доска читалась как «две важные и куча
 * одинаковых».
 *
 * Порядок тонов повторяет срочность: красный → янтарный → синий → фиолетовый →
 * нейтральный.
 */
export const BUCKET_TONES: Record<Bucket, ITaskTone> = {
  overdue: {
    fg: 'var(--k-fg-b91c1c)',
    bg: 'var(--k-bg-fef2f2)',
    line: 'var(--k-border-dc2626)',
  },
  today: {
    fg: 'var(--k-fg-b45309)',
    bg: 'var(--k-bg-fffbeb)',
    line: 'var(--k-border-d97706)',
  },
  tomorrow: {
    fg: 'var(--k-fg-1d4ed8)',
    bg: 'var(--k-bg-eff6ff)',
    line: 'var(--k-border-2563eb)',
  },
  thisWeek: {
    fg: 'var(--k-fg-6d28d9)',
    bg: 'var(--k-bg-f5f3ff)',
    line: 'var(--k-border-7c3aed)',
  },
  later: NEUTRAL_TONE,
  noDate: NEUTRAL_TONE,
  done: {
    fg: 'var(--k-fg-047857)',
    bg: 'var(--k-bg-d1fae5)',
    line: 'var(--k-border-059669)',
  },
};

/**
 * Тона статусов. «Запланировано» намеренно СЕРО-СИНЕЕ, а не синее: синий здесь
 * уже занят работой, и два синих рядом не различаются.
 */
export const STATUS_TONES: Record<string, ITaskTone> = {
  open: {
    fg: 'var(--k-fg-475569)',
    bg: 'var(--k-bg-f1f5f9)',
    line: 'var(--k-border-94a3b8)',
  },
  in_progress: {
    fg: 'var(--k-fg-1d4ed8)',
    bg: 'var(--k-bg-dbeafe)',
    line: 'var(--k-border-2563eb)',
  },
  done: {
    fg: 'var(--k-fg-047857)',
    bg: 'var(--k-bg-d1fae5)',
    line: 'var(--k-border-059669)',
  },
  cancelled: {
    fg: 'var(--k-fg-475569)',
    bg: 'var(--k-bg-f1f5f9)',
    line: 'var(--k-border-cbd5e1)',
  },
};

/**
 * Тон предупреждения — «есть несохранённые изменения».
 *
 * 🔴 Отдельный, а не «янтарный бакет Сегодня». Когда чип взял тон колонки,
 * фон побледнел с #fef3c7 до #fffbeb, и предупреждение стало почти
 * незаметным. Колонка и предупреждение решают разные задачи: у колонки фон —
 * подложка под заголовок, у чипа — единственный способ докричаться.
 */
export const ATTENTION_TONE: ITaskTone = {
  fg: 'var(--k-fg-92400e)',
  bg: 'var(--k-bg-fef3c7)',
  line: 'var(--k-border-d97706)',
};

/**
 * Тон шапки колонки по её ключу. Колонки доски бывают двух группировок —
 * бакеты сроков и статусы, — и ключ однозначно называет одну из них. Ключ
 * `done` есть в обеих картах и в обеих зелёный, так что порядок поиска на него
 * не влияет.
 */
export const columnTone = (key: string): ITaskTone =>
  BUCKET_TONES[key as Bucket] || STATUS_TONES[key] || NEUTRAL_TONE;

/** Чип «Высокий» (ТЗ 0111 вернуло приоритет): красный, как в 0066. */
export const HIGH_PRIORITY_TONE: ITaskTone = {
  fg: 'var(--k-fg-b91c1c)',
  bg: 'var(--k-bg-fee2e2)',
  line: 'var(--k-border-dc2626)',
};

/** Признаки, которые карточка доски и строка списка показывают чипом. */
export type TChipTone = 'warn' | 'accent' | 'high';

/**
 * Тон чипа. ОДНА функция на оба вида — и в этом весь смысл.
 *
 * 🔴 Пока их было две (по одной в `TaskBoard.styled` и `TaskList.styled`), они
 * разъехались: «в работе» — `#1e3a8a` на доске и `#1d4ed8` в списке. Разъехались
 * молча: правка одного файла ничем не касалась второго. Теперь у них общее
 * тело, и разойтись им негде.
 */
export const chipTone = (tone?: TChipTone): ITaskTone => {
  if (tone === 'accent') return STATUS_TONES.in_progress;
  if (tone === 'warn') return ATTENTION_TONE;
  if (tone === 'high') return HIGH_PRIORITY_TONE;
  return NEUTRAL_TONE;
};

/**
 * Левая кромка и фон карточки по сроку (ТЗ 0109).
 *
 * Цвет говорит только о том, что требует внимания: просрочка — красная кромка
 * и бледно-красный фон, «сегодня» — янтарная кромка, закрытая — приглушённая
 * зелёная. Остальные сроки кромки не получают: подсветить всё значит не
 * подсветить ничего.
 */
export interface ICardEdgeTone {
  /** Цвет левой кромки; `transparent` — кромки нет. */
  edge: string;
  /** Фон карточки. */
  fill: string;
}

export const cardEdgeTone = (bucket: Bucket): ICardEdgeTone => {
  if (bucket === 'overdue') {
    return { edge: BUCKET_TONES.overdue.line, fill: BUCKET_TONES.overdue.bg };
  }
  if (bucket === 'today') {
    return { edge: BUCKET_TONES.today.line, fill: SURFACE.card };
  }
  if (bucket === 'done') {
    return { edge: 'var(--k-bg-a7f3d0)', fill: SURFACE.card };
  }
  return { edge: 'transparent', fill: SURFACE.card };
};

/**
 * Палитра аватаров исполнителей (ТЗ 0109). Тон выбирается по id сотрудника,
 * а не по имени: тёзки различаются, а переименование не перекрашивает
 * человека. Цвета насыщенные ровно настолько, чтобы белые инициалы читались
 * (контраст ≥ 4.5 на 11px полужирном).
 */
export const AVATAR_PALETTE: readonly string[] = [
  '#1d4ed8',
  '#6d28d9',
  '#be185d',
  '#b45309',
  '#047857',
  '#0e7490',
  '#4338ca',
  '#9f1239',
];

/** Детерминированный тон аватара: один и тот же id — всегда один цвет. */
export const avatarTone = (id?: string | null): string => {
  const key = String(id ?? '');
  let h = 0;
  for (let i = 0; i < key.length; i += 1) {
    h = (h * 31 + key.charCodeAt(i)) % 100003;
  }
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

/**
 * Тона чипов связей по виду (ТЗ 0109). Люди — синие, учёба — фиолетовые,
 * деньги и договоры — янтарные, дофинансирование — бирюзовые, маркетинг и
 * лиды — розовые. Незнакомый вид — нейтральный.
 */
const PEOPLE: ITaskTone = {
  fg: 'var(--k-fg-1d4ed8)',
  bg: 'var(--k-bg-eff6ff)',
  line: 'var(--k-border-bfdbfe)',
};
const STUDY: ITaskTone = {
  fg: 'var(--k-fg-6d28d9)',
  bg: 'var(--k-bg-f5f3ff)',
  line: 'var(--k-border-ddd6fe)',
};
const PAPER: ITaskTone = {
  fg: 'var(--k-fg-b45309)',
  bg: 'var(--k-bg-fffbeb)',
  line: 'var(--k-border-fde68a)',
};
const FUNDING: ITaskTone = {
  fg: 'var(--k-fg-0e7490)',
  bg: 'var(--k-bg-ecfeff)',
  line: 'var(--k-border-a5f3fc)',
};
const SALES: ITaskTone = {
  fg: 'var(--k-fg-be185d)',
  bg: 'var(--k-bg-fdf2f8)',
  line: 'var(--k-border-fbcfe8)',
};

export const LINK_KIND_TONES: Record<string, ITaskTone> = {
  student: PEOPLE,
  teacher: PEOPLE,
  group: STUDY,
  course: STUDY,
  lesson: STUDY,
  lesson_recording: STUDY,
  survey: STUDY,
  agreement: PAPER,
  vacancy: PAPER,
  funding_operator: FUNDING,
  funding_candidate: FUNDING,
  funding_intake: FUNDING,
  marketing_signal: SALES,
  kommo_lead: SALES,
  ai_finding: SALES,
  // ТЗ 0122: письмо — переписка, как лид; договор на подпись — бумага.
  mail_thread: SALES,
  contract: PAPER,
};

export const linkKindTone = (kind: string): ITaskTone =>
  LINK_KIND_TONES[kind] || NEUTRAL_TONE;

/** Индикатор автосохранения окна задачи (ТЗ 0109). */
export const SAVE_STATE_TONES: Record<'saving' | 'saved' | 'error', string> = {
  saving: SURFACE.muted,
  saved: STATUS_TONES.done.fg,
  error: BUCKET_TONES.overdue.fg,
};
